import { useState, useEffect } from 'react';
import { Form, Select, Input, Alert, App, Tag, DatePicker, Divider, Space, Collapse, Tabs, InputNumber } from 'antd';
import { ExclamationCircleOutlined, CalendarOutlined, RocketOutlined, SettingOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import dayjs, { type Dayjs } from 'dayjs';
import { withDevModalFilePath } from '../../../components/common';
import { getMutationPendingState } from '../../../utils/trpcMutation';

const { Modal } = withDevModalFilePath('client/src/pages/admin/companies/SubscriptionStatusModal.tsx');

interface SubscriptionStatusModalProps {
  open: boolean;
  companyId: string;
  companyName: string;
  currentStatus: string | null;
  currentPlan?: string | null;
  currentDates: {
    subscriptionStartsAt: string | Date | null;
    subscriptionEndsAt: string | Date | null;
    subscriptionCanceledAt: string | Date | null;
  };
  currentAddons?: {
    predictionAddonUnits: number | null;
    userAddonUnits: number | null;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const subscriptionStatuses = [
  { value: 'trialing', label: '無料登録', color: 'processing' },
  { value: 'active', label: 'アクティブ', color: 'success' },
  { value: 'past_due', label: '支払遅延', color: 'warning' },
  { value: 'canceled', label: 'キャンセル済', color: 'error' },
  { value: 'suspended', label: '停止中', color: 'default' },
] as const;

const subscriptionPlans = [
  { value: 'free', label: 'Free', color: 'default' },
  { value: 'starter', label: 'Starter', color: 'blue' },
  { value: 'standard', label: 'Standard', color: 'cyan' },
  { value: 'professional', label: 'Professional', color: 'green' },
  { value: 'enterprise', label: 'Enterprise', color: 'purple' },
  { value: 'enterprise_premium', label: 'Enterprise Premium', color: 'gold' },
] as const;

type SubscriptionStatus = typeof subscriptionStatuses[number]['value'];
const toDateValue = (value: string | Date | null | undefined): Dayjs | null => {
  if (!value) return null;
  return dayjs(value);
};

const toIsoString = (value: Dayjs | null): string | null => {
  if (!value) return null;
  return value.toISOString();
};

export const SubscriptionStatusModal: React.FC<SubscriptionStatusModalProps> = ({
  open,
  companyId,
  companyName,
  currentStatus,
  currentPlan,
  currentDates,
  currentAddons,
  onClose,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [statusForm] = Form.useForm();
  const [planForm] = Form.useForm();
  const [selectedStatus, setSelectedStatus] = useState<SubscriptionStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'status' | 'plan'>('plan');
  // ステータス変更用ミューテーション
  const updateStatusMutation = trpc.subscriptionAdmin.updateSubscriptionStatus.useMutation({
    onSuccess: (data) => {
      if (data.warnings && data.warnings.length > 0) {
        message.warning(`ステータスを変更しました。警告: ${data.warnings.join(', ')}`);
      } else {
        message.success(`ステータスを ${data.oldStatus} → ${data.newStatus} に変更しました`);
      }
      onSuccess();
      onClose();
    },
    onError: (error) => {
      message.error(error.message ?? 'ステータスの変更に失敗しました');
    },
  });

  // 手動プラン有効化用ミューテーション
  const activateManualPlanMutation = trpc.subscriptionAdmin.activateManualPlan.useMutation({
    onSuccess: (data) => {
      message.success(`プラン「${data.planCode}」を有効化しました（Stripe課金なし）`);
      onSuccess();
      onClose();
    },
    onError: (error) => {
      message.error(error.message ?? 'プランの有効化に失敗しました');
    },
  });

  useEffect(() => {
    if (open) {
      // ステータス変更フォームの初期化
      statusForm.resetFields();
      setSelectedStatus(currentStatus as SubscriptionStatus);
      statusForm.setFieldsValue({
        subscriptionStatus: currentStatus,
        subscriptionStartsAt: toDateValue(currentDates.subscriptionStartsAt),
        subscriptionEndsAt: toDateValue(currentDates.subscriptionEndsAt),
        subscriptionCanceledAt: toDateValue(currentDates.subscriptionCanceledAt),
        reason: '',
      });

      // 手動プラン有効化フォームの初期化
      planForm.resetFields();
      planForm.setFieldsValue({
        planCode: currentPlan ?? 'starter',
        subscriptionStartsAt: dayjs(),
        subscriptionEndsAt: null,
        predictionAddonUnits: currentAddons?.predictionAddonUnits ?? 0,
        userAddonUnits: currentAddons?.userAddonUnits ?? 0,
        memo: '',
      });
    }
  }, [open, currentStatus, currentPlan, currentDates, currentAddons, statusForm, planForm]);

  const handleStatusOk = async () => {
    try {
      const values = await statusForm.validateFields();

      const subscriptionStartsAt = values.subscriptionStartsAt;
      const subscriptionEndsAt = values.subscriptionEndsAt;
      const subscriptionCanceledAt = values.subscriptionCanceledAt;

      const hasSubscriptionStartsAtChanged = toIsoString(subscriptionStartsAt) !== toIsoString(toDateValue(currentDates.subscriptionStartsAt));
      const hasSubscriptionEndsAtChanged = toIsoString(subscriptionEndsAt) !== toIsoString(toDateValue(currentDates.subscriptionEndsAt));
      const hasSubscriptionCanceledAtChanged = toIsoString(subscriptionCanceledAt) !== toIsoString(toDateValue(currentDates.subscriptionCanceledAt));

      await updateStatusMutation.mutateAsync({
        companyId,
        subscriptionStatus: values.subscriptionStatus,
        subscriptionStartsAt: hasSubscriptionStartsAtChanged ? toIsoString(subscriptionStartsAt) : undefined,
        subscriptionEndsAt: hasSubscriptionEndsAtChanged ? toIsoString(subscriptionEndsAt) : undefined,
        subscriptionCanceledAt: hasSubscriptionCanceledAtChanged ? toIsoString(subscriptionCanceledAt) : undefined,
        reason: values.reason || undefined,
      });
    } catch {
      // バリデーションエラー
    }
  };

  const handlePlanOk = async () => {
    try {
      const values = await planForm.validateFields();

      await activateManualPlanMutation.mutateAsync({
        companyId,
        planCode: values.planCode,
        subscriptionStartsAt: values.subscriptionStartsAt ? toIsoString(values.subscriptionStartsAt) : undefined,
        subscriptionEndsAt: values.subscriptionEndsAt ? toIsoString(values.subscriptionEndsAt) : undefined,
        predictionAddonUnits: values.predictionAddonUnits ?? 0,
        userAddonUnits: values.userAddonUnits ?? 0,
        memo: values.memo || undefined,
      });
    } catch {
      // バリデーションエラー
    }
  };

  const handleOk = () => {
    if (activeTab === 'status') {
      handleStatusOk();
    } else {
      handlePlanOk();
    }
  };

  const currentStatusInfo = subscriptionStatuses.find(s => s.value === currentStatus);
  const selectedStatusInfo = subscriptionStatuses.find(s => s.value === selectedStatus);
  const isStatusChanged = selectedStatus && selectedStatus !== currentStatus;
  const currentPlanInfo = subscriptionPlans.find(p => p.value === currentPlan);

  const isLoading =
    getMutationPendingState(updateStatusMutation) ||
    getMutationPendingState(activateManualPlanMutation);

  return (
    <Modal
      title="契約管理（手動）"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={activeTab === 'plan' ? 'プランを有効化' : '変更する'}
      cancelText="キャンセル"
      confirmLoading={isLoading}
      okButtonProps={{
        danger: activeTab === 'status' && selectedStatus === 'canceled',
      }}
      width={650}
    >
      <div style={{ marginBottom: 16 }}>
        <strong>会社名:</strong> {companyName}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space size="large">
          <span>
            <strong>現在のプラン:</strong>{' '}
            {currentPlanInfo ? (
              <Tag color={currentPlanInfo.color}>{currentPlanInfo.label}</Tag>
            ) : (
              <Tag>未設定</Tag>
            )}
          </span>
          <span>
            <strong>現在のステータス:</strong>{' '}
            {currentStatusInfo ? (
              <Tag color={currentStatusInfo.color}>{currentStatusInfo.label}</Tag>
            ) : (
              <Tag>未設定</Tag>
            )}
          </span>
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'status' | 'plan')}
        items={[
          {
            key: 'plan',
            label: (
              <span>
                <RocketOutlined />
                手動プラン有効化
              </span>
            ),
            children: (
              <>
                <Alert
                  type="info"
                  icon={<RocketOutlined />}
                  message="Stripe課金なしでプランを有効化"
                  description="協力会社テスト、デモ環境、特別契約用。課金は行われず、契約情報のみ更新されます。"
                  style={{ marginBottom: 16 }}
                  showIcon
                />

                <Form form={planForm} layout="vertical">
                  <Form.Item
                    name="planCode"
                    label="プラン"
                    rules={[{ required: true, message: 'プランを選択してください' }]}
                  >
                    <Select
                      placeholder="プランを選択"
                      options={subscriptionPlans.map(p => ({
                        value: p.value,
                        label: (
                          <span>
                            <Tag color={p.color} style={{ marginRight: 8 }}>{p.label}</Tag>
                            {p.value === currentPlan && '(現在)'}
                          </span>
                        ),
                      }))}
                    />
                  </Form.Item>

                  <Space size="middle" style={{ width: '100%' }}>
                    <Form.Item
                      name="subscriptionStartsAt"
                      label="契約開始日"
                      style={{ flex: 1 }}
                    >
                      <DatePicker
                        showTime
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                        placeholder="開始日を選択"
                      />
                    </Form.Item>

                    <Form.Item
                      name="subscriptionEndsAt"
                      label="契約終了日（任意）"
                      style={{ flex: 1 }}
                    >
                      <DatePicker
                        showTime
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                        placeholder="終了日を選択"
                        allowClear
                      />
                    </Form.Item>
                  </Space>

                  <Divider orientation="left" plain>アドオン設定</Divider>

                  <Space size="middle" style={{ width: '100%' }}>
                    <Form.Item
                      name="predictionAddonUnits"
                      label="健全度予測アドオン（ユニット数）"
                      style={{ flex: 1 }}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name="userAddonUnits"
                      label="ユーザーアドオン（ユニット数）"
                      style={{ flex: 1 }}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Space>

                  <Form.Item
                    name="memo"
                    label="メモ（オプション）"
                  >
                    <Input.TextArea
                      placeholder="協力会社テスト用、デモ環境など"
                      rows={2}
                      maxLength={500}
                      showCount
                    />
                  </Form.Item>
                </Form>
              </>
            ),
          },
          {
            key: 'status',
            label: (
              <span>
                <SettingOutlined />
                ステータス変更（デバッグ）
              </span>
            ),
            children: (
              <>
                <Alert
                  type="warning"
                  icon={<ExclamationCircleOutlined />}
                  message="デバッグ用機能"
                  description="この機能はデバッグ用です。本番環境での使用には注意してください。変更は監査ログに記録されます。"
                  style={{ marginBottom: 16 }}
                  showIcon
                />

                <Form form={statusForm} layout="vertical">
                  <Form.Item
                    name="subscriptionStatus"
                    label="新しいステータス"
                    rules={[{ required: true, message: 'ステータスを選択してください' }]}
                  >
                    <Select
                      placeholder="ステータスを選択"
                      onChange={(value) => setSelectedStatus(value as SubscriptionStatus)}
                      options={subscriptionStatuses.map(s => ({
                        value: s.value,
                        label: (
                          <span>
                            <Tag color={s.color} style={{ marginRight: 8 }}>{s.label}</Tag>
                            {s.value === currentStatus && '(現在)'}
                          </span>
                        ),
                      }))}
                    />
                  </Form.Item>

                  {isStatusChanged && (
                    <div style={{ marginBottom: 16 }}>
                      <strong>変更後:</strong>{' '}
                      {currentStatusInfo && (
                        <>
                          <Tag color={currentStatusInfo.color}>{currentStatusInfo.label}</Tag>
                          <span style={{ margin: '0 8px' }}>→</span>
                        </>
                      )}
                      {selectedStatusInfo && (
                        <Tag color={selectedStatusInfo.color}>{selectedStatusInfo.label}</Tag>
                      )}
                    </div>
                  )}

                  {selectedStatus === 'canceled' && (
                    <Alert
                      type="error"
                      message="キャンセル済に変更すると..."
                      description={
                        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                          <li>管理者以外のユーザーはログインできなくなります</li>
                          <li>管理者は契約プランページのみアクセス可能になります</li>
                          <li>再契約フローから復帰できます</li>
                        </ul>
                      }
                      style={{ marginBottom: 16 }}
                      showIcon
                    />
                  )}

                  <Divider orientation="left" plain>
                    <Space>
                      <CalendarOutlined />
                      関連日付
                    </Space>
                  </Divider>

                  <Collapse
                    ghost
                    defaultActiveKey={['dates']}
                    items={[
                      {
                        key: 'dates',
                        label: '日付を編集（オプション）',
                        children: (
                          <>
                            <Form.Item
                              name="subscriptionStartsAt"
                              label="契約開始日"
                              tooltip="有料契約の開始日。active ステータスの場合に必要です。"
                            >
                              <DatePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                style={{ width: '100%' }}
                                placeholder="契約開始日を選択"
                                allowClear
                              />
                            </Form.Item>

                            <Form.Item
                              name="subscriptionEndsAt"
                              label="契約終了日"
                              tooltip="契約期間の終了日（次回更新日）。"
                            >
                              <DatePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                style={{ width: '100%' }}
                                placeholder="契約終了日を選択"
                                allowClear
                              />
                            </Form.Item>

                            <Form.Item
                              name="subscriptionCanceledAt"
                              label="解約日時"
                              tooltip="解約が実行された日時。canceled ステータスの場合に設定されます。"
                            >
                              <DatePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                style={{ width: '100%' }}
                                placeholder="解約日時を選択"
                                allowClear
                              />
                            </Form.Item>

                            <Alert
                              type="info"
                              message="整合性チェック"
                              description={
                                <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                                  <li>契約開始日 ≤ 契約終了日</li>
                                  <li>canceled の場合は解約日時が自動設定されます</li>
                                  <li>active で契約開始日がない場合は自動設定されます</li>
                                </ul>
                              }
                              style={{ marginBottom: 0 }}
                            />
                          </>
                        ),
                      },
                    ]}
                  />

                  <Divider />

                  <Form.Item
                    name="reason"
                    label="変更理由（オプション）"
                  >
                    <Input.TextArea
                      placeholder="デバッグのため、テスト目的など"
                      rows={3}
                      maxLength={500}
                      showCount
                    />
                  </Form.Item>
                </Form>
              </>
            ),
          },
        ]}
      />
    </Modal>
  );
};
